import React, { useState, useMemo } from 'react';

/**
 * Excel-like filterable/sortable table component with inline editing
 * Filters come from parent via FilterBar, table just handles sorting and display
 * Dynamic action buttons based on inspection status
 * Shows status badge only when viewing "All" (no actions on mixed view)
 * INLINE EDITING: Click any editable cell to edit (Airtable-style)
 */
const ExcelTable = ({ 
  data, 
  totalCount, // ✅ Total count before pagination
  columns, 
  expandableColumns = [], // Columns to show in expanded details
  filters = {}, 
  onApprove, 
  onReject, 
  onUndo,
  onDelete,
  onArchive,
  onExport, // ✅ NEW: Export selected inspections
  onReviewDuplicates, // 🆕 Callback to open duplicate review modal
  currentStatusFilter, 
  loading,
  editingCell,
  setEditingCell,
  editValue,
  setEditValue,
  onCellSave
}) => {
  const [sortColumn, setSortColumn] = useState('capture_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  const [columnWidths, setColumnWidths] = useState({}); // Track column widths
  const [resizingColumn, setResizingColumn] = useState(null); // Track which column is being resized

  // Handle sort column click
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('desc');
    }
  };


  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // Apply filters
    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (!filterValue || filterValue === '') return;

      result = result.filter(row => {
        const cellValue = row[key];
        if (cellValue == null) return false;

        // Text filter (case-insensitive contains)
        if (typeof filterValue === 'string') {
          return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
        }

        // Date range filter
        if (filterValue.from || filterValue.to) {
          const cellDate = new Date(cellValue);
          if (filterValue.from && cellDate < new Date(filterValue.from)) return false;
          if (filterValue.to && cellDate > new Date(filterValue.to)) return false;
        }

        return true;
      });
    });

    // Apply sorting
    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        // Handle null/undefined
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Try date comparison first
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        if (!isNaN(aDate) && !isNaN(bDate)) {
          return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        }

        // String comparison
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, filters, sortColumn, sortDirection]);

  // Select all/none
  const toggleSelectAll = () => {
    if (selectedRows.length === filteredAndSortedData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredAndSortedData.map(row => row.id));
    }
  };

  // Toggle individual row
  const toggleRow = (id) => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!onDelete || selectedRows.length === 0) return;
    
    const confirmed = window.confirm(
      `Delete ${selectedRows.length} inspection(s)?\n\nThey will be moved to trash and can be restored later.`
    );
    
    if (confirmed) {
      await onDelete(selectedRows);
      setSelectedRows([]); // Clear selection after delete
    }
  };

  // Handle bulk archive
  const handleBulkArchive = async () => {
    if (!onArchive || selectedRows.length === 0) return;
    
    const confirmed = window.confirm(
      `Archive ${selectedRows.length} inspection(s)?\n\nThey will remain in the database but be marked as archived.`
    );
    
    if (confirmed) {
      await onArchive(selectedRows);
      setSelectedRows([]); // Clear selection after archive
    }
  };

  // Column resizing handlers
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setResizingColumn(columnKey);
    const startX = e.pageX;
    const startWidth = columnWidths[columnKey] || 150;

    const handleMouseMove = (e) => {
      const diff = e.pageX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Min width 50px
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-white/60">Loading inspections...</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse" style={{ tableLayout: 'auto', width: '100%' }}>
          <thead className="sticky top-0 z-10">
            <tr className="bg-[var(--pbfs-navy)] backdrop-blur-md shadow-lg border-b-2 border-[var(--pbfs-green)]/30">
            {/* Select all checkbox */}
            <th className="px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedRows.length === filteredAndSortedData.length && filteredAndSortedData.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4"
              />
            </th>

            {/* Expand/Collapse column */}
            {expandableColumns.length > 0 && (
              <th className="px-2 py-3 text-left w-10">
                {/* Empty header for expand button column */}
              </th>
            )}

            {/* Column headers with sort indicator and resize handle */}
            {columns.map((col, colIndex) => (
              <th 
                key={col.key} 
                className="px-4 py-3 text-left relative group whitespace-nowrap"
                style={{ width: columnWidths[col.key] || 'auto', minWidth: col.key === 'thumbnail_url' ? '180px' : '100px' }}
              >
                <button
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                  className="flex items-center gap-2 text-white/90 hover:text-white font-medium text-sm transition-colors"
                >
                  {col.label}
                  {sortColumn === col.key && (
                    <span className="text-[var(--pbfs-green)] text-lg">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                  {filters[col.key] && (
                    <span className="text-[var(--pbfs-green)] text-xs">🔍</span>
                  )}
                </button>
                {/* Resize handle - PBFS Green visible on ALL columns including first */}
                {colIndex < columns.length - 1 && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-[3px] cursor-col-resize bg-[var(--pbfs-green)]/40 hover:bg-[var(--pbfs-green)] transition-colors z-50"
                    onMouseDown={(e) => handleMouseDown(e, col.key)}
                    title="Drag to resize column"
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {filteredAndSortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 2} className="px-4 py-12 text-center text-white/40">
                No inspections found
                {Object.keys(filters).length > 0 && ' matching your filters'}
              </td>
            </tr>
          ) : (
            filteredAndSortedData.map((row, idx) => {
              const rowId = row.id || row._id; // Support both id formats
              const isExpanded = expandedRows.has(rowId);
              return (
                <React.Fragment key={rowId}>
                  <tr
                    className={`border-b border-white/10 hover:bg-white/5 transition-colors ${
                      idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                    }`}
                  >
                    {/* Select checkbox */}
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(rowId)}
                        onChange={() => toggleRow(rowId)}
                        className="w-4 h-4"
                      />
                    </td>

                    {/* Expand/Collapse button */}
                    {expandableColumns.length > 0 && (
                      <td className="px-2 py-3">
                        <button
                          onClick={() => toggleRowExpansion(rowId)}
                          className="text-white/60 hover:text-[var(--pbfs-green)] transition-colors text-lg"
                          title={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </td>
                    )}

                    {/* Data cells - with inline editing support */}
                    {columns.map(col => {
                      const isEditing = editingCell?.rowId === row._id && editingCell?.columnKey === col.key;
                      const isEditable = col.editable !== false && onCellSave;
                      const hasSpecialEdit = col.specialEdit; // For combined columns with one editable field
                      const cellValue = row[col.key];
                      const isImageColumn = col.key === 'thumbnail_url';
                      
                      // Special click handler for combined date column
                      const handleCellClick = (e) => {
                        if (isImageColumn) return;
                        
                        // Check if this is the "All Dates" column with special edit
                        if (hasSpecialEdit === 'last_inspection_date') {
                          // Only edit if clicking on the "Last Insp" row
                          const target = e.target;
                          const lastInspRow = target.closest('[data-field="last_inspection"]');
                          
                          if (lastInspRow) {
                            setEditingCell({ rowId: row._id, columnKey: 'last_inspection_date' });
                            // Format date for input (YYYY-MM-DD)
                            const dateVal = row.last_inspection_date;
                            let formattedDate = '';
                            if (dateVal) {
                              if (typeof dateVal === 'string') {
                                formattedDate = dateVal;
                              } else if (dateVal.year && dateVal.month && dateVal.day) {
                                formattedDate = `${dateVal.year}-${String(dateVal.month).padStart(2, '0')}-${String(dateVal.day).padStart(2, '0')}`;
                              }
                            }
                            setEditValue(formattedDate);
                          }
                        } else if (isEditable && !isEditing) {
                          setEditingCell({ rowId: row._id, columnKey: col.key });
                          const initialValue = col.editRender ? col.editRender(cellValue) : (cellValue || '');
                          setEditValue(String(initialValue));
                        }
                      };
                      
                      return (
                        <td 
                          key={col.key} 
                          className={`px-4 py-3 text-sm text-white/80 ${(isEditable && !hasSpecialEdit) ? 'cursor-pointer hover:bg-[var(--pbfs-green)]/5' : ''} ${isImageColumn ? 'w-32' : ''}`}
                          onClick={hasSpecialEdit ? undefined : handleCellClick}
                          title={(isEditable && !hasSpecialEdit) && !isImageColumn ? 'Click to edit' : ''}
                        >
                          {isImageColumn ? (
                            // Image column with status badge and actions
                            <div className="flex items-center gap-3">
                              <div>
                                {col.render ? col.render(cellValue, row) : (cellValue || '—')}
                              </div>
                              {/* Status badge and actions stacked */}
                              <div className="flex flex-col gap-1.5">
                                {/* Status Badge */}
                                {row.status === 'pending' || row.status === 'analyzed' || row.status === 'needs_manual_review' ? (
                                  <span className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/40 text-yellow-400 text-xs rounded font-medium text-center">
                                    Pending
                                  </span>
                                ) : row.status === 'approved' ? (
                                  <span className="px-2 py-1 bg-green-500/20 border border-green-500/40 text-green-400 text-xs rounded font-medium text-center">
                                    ✅ Approved{row.approval_source === 'ai' || row.approved_auto || row.approved_by === 'AI' ? ' (AI)' : row.approval_source === 'user' || row.approved_by_user ? ' (User)' : row.approval_source === 'admin' || row.approved_by ? ' (Admin)' : ''}
                                  </span>
                                ) : row.status === 'rejected' ? (
                                  <span
                                    className="px-2 py-1 bg-red-500/20 border border-red-500/40 text-red-400 text-xs rounded font-medium text-center"
                                    title={row.rejection_reason ? `Reason: ${row.rejection_reason}` : ''}
                                  >
                                    ❌ Rejected{row.rejection_source === 'ai' || row.rejected_auto ? ' (AI)' : row.rejection_source === 'admin' ? ' (Admin)' : ''}
                                  </span>
                                ) : (
                                  <span className="text-white/40 text-xs italic">Unknown</span>
                                )}

                                {/* Duplicate Badge - Clickable to review duplicates */}
                                {row.duplicate_status && row.duplicate_status !== 'none' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onReviewDuplicates) {
                                        onReviewDuplicates(row);
                                      }
                                    }}
                                    className={`px-2 py-1 text-xs rounded font-medium text-center transition-all hover:scale-105 active:scale-95 cursor-pointer ${
                                      row.duplicate_status === 'canonical' ? 'bg-purple-500/20 border border-purple-500/40 text-purple-400 hover:bg-purple-500/30' :
                                      row.duplicate_status === 'confirmed_duplicate' ? 'bg-orange-500/20 border border-orange-500/40 text-orange-400 hover:bg-orange-500/30' :
                                      row.duplicate_status === 'suspected' ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30' :
                                      row.duplicate_status === 'not_duplicate' ? 'bg-gray-500/20 border border-gray-500/40 text-gray-400 hover:bg-gray-500/30' :
                                      'bg-gray-500/20 border border-gray-500/40 text-gray-400'
                                    }`}
                                    title={row.duplicate_reason ? `Click to review: ${row.duplicate_reason}` : 'Click to review duplicate group'}
                                  >
                                    {row.duplicate_status === 'canonical' ? '🎯 Canonical' :
                                     row.duplicate_status === 'confirmed_duplicate' ? '🔄 Duplicate' :
                                     row.duplicate_status === 'suspected' ? '❓ Suspected' :
                                     row.duplicate_status === 'not_duplicate' ? '✅ Not Duplicate' :
                                     row.duplicate_status}
                                  </button>
                                )}
                                
                                {/* Action Buttons */}
                                {(row.status === 'pending' || row.status === 'analyzed' || row.status === 'needs_manual_review') && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onApprove(rowId);
                                      }}
                                      className="px-2 py-1 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/80 text-white text-xs rounded transition-all font-medium shadow-md hover:shadow-lg whitespace-nowrap"
                                      title="Approve this tag lead"
                                    >
                                      ✅ Approve
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onReject(rowId);
                                      }}
                                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-all font-medium shadow-md hover:shadow-lg whitespace-nowrap"
                                      title="Reject this tag lead"
                                    >
                                      ❌ Reject
                                    </button>
                                  </>
                                )}
                                {row.status === 'approved' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onUndo(rowId, 'approved');
                                    }}
                                    className="px-1.5 py-0.5 text-white/50 hover:text-white/80 text-[10px] transition-colors whitespace-nowrap"
                                    title="Move back to pending"
                                  >
                                    ↩ undo
                                  </button>
                                )}
                                {row.status === 'rejected' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onUndo(rowId, 'rejected');
                                    }}
                                    className="px-1.5 py-0.5 text-white/50 hover:text-white/80 text-[10px] transition-colors whitespace-nowrap"
                                    title="Move back to pending"
                                  >
                                    ↩ undo
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : isEditing ? (
                            // NORMAL EDITING MODE - Text input
                            <input
                              type={col.editType || 'text'}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => {
                                if (editValue !== (cellValue || '')) {
                                  onCellSave(row._id, col.key, editValue);
                                } else {
                                  setEditingCell(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  onCellSave(row._id, col.key, editValue);
                                } else if (e.key === 'Escape') {
                                  setEditingCell(null);
                                  setEditValue('');
                                }
                              }}
                              autoFocus
                              className="w-full px-2 py-1 bg-[var(--pbfs-navy)] border border-[var(--pbfs-green)] rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                            />
                          ) : (isEditing && editingCell?.columnKey === 'last_inspection_date' && col.key === 'all_dates') ? (
                            // DATE PICKER MODE - Special editing for last inspection date in combined column
                            <input
                              type="date"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => {
                                if (editValue) {
                                  onCellSave(row._id, 'last_inspection_date', editValue);
                                } else {
                                  setEditingCell(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  onCellSave(row._id, 'last_inspection_date', editValue);
                                } else if (e.key === 'Escape') {
                                  setEditingCell(null);
                                  setEditValue('');
                                }
                              }}
                              autoFocus
                              className="w-full px-2 py-1 bg-[var(--pbfs-navy)] border border-[var(--pbfs-green)] rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--pbfs-green)]/50"
                              style={{ colorScheme: 'dark' }}
                            />
                          ) : (
                            // DISPLAY MODE - Show value with edit indicator
                            <div className={`flex items-center gap-2 ${isEditable || hasSpecialEdit ? 'group' : ''}`}>
                              <span>
                                {col.render ? col.render(cellValue, row, editingCell, setEditingCell, setEditValue, editValue, onCellSave) : (cellValue || '—')}
                              </span>
                              {(isEditable || hasSpecialEdit) && (
                                <span className="opacity-0 group-hover:opacity-100 text-[var(--pbfs-green)] text-xs transition-opacity">
                                  ✏️
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Expanded details row */}
                  {isExpanded && expandableColumns.length > 0 && (
                    <tr className="bg-white/[0.03] border-b border-white/10">
                      <td colSpan={1 + (expandableColumns.length > 0 ? 1 : 0) + columns.length} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {expandableColumns.map(col => {
                            const cellValue = row[col.key];
                            return (
                              <div key={col.key} className="flex flex-col">
                                <span className="text-white/60 text-xs mb-1 font-medium">{col.label}:</span>
                                <span className="text-white/90 text-sm">
                                  {col.render ? col.render(cellValue, row) : (cellValue || '—')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      </div>

      {/* Results summary - Fixed at bottom */}
      <div className="flex-shrink-0 bg-[var(--pbfs-navy)] py-3 px-4 border-t border-white/10 flex items-center justify-between">
        <div className="text-white/60 text-sm">
          Showing {filteredAndSortedData.length} of {totalCount || data.length} inspections
          {selectedRows.length > 0 && ` • ${selectedRows.length} selected`}
        </div>
        
        {/* Bulk Actions - Show when rows are selected */}
        {selectedRows.length > 0 && (onDelete || onArchive || onExport) && (
          <div className="flex gap-2">
            {onExport && (
              <button
                onClick={() => onExport(selectedRows)}
                className="px-4 py-2 bg-[var(--pbfs-green)]/20 hover:bg-[var(--pbfs-green)]/30 text-[var(--pbfs-green)] rounded-lg transition-colors text-sm font-medium"
                title="Export selected inspections to CSV"
              >
                📊 Export ({selectedRows.length})
              </button>
            )}
            {onArchive && (
              <button
                onClick={handleBulkArchive}
                className="px-4 py-2 bg-[var(--pbfs-blue)]/20 hover:bg-[var(--pbfs-blue)]/30 text-[var(--pbfs-blue)] rounded-lg transition-colors text-sm font-medium"
              >
                📁 Archive ({selectedRows.length})
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm font-medium"
              >
                🗑️ Delete ({selectedRows.length})
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelTable;

